import { useAppSettingsDictionary } from '@/shared/api';
import { useMemo } from 'react';
import type { AccountOption } from './types';
import type { ApiTokenAccessKey } from '@/shared/types';
import {
  ACCOUNT_TYPES,
  useAccountsRests,
  type AccountsMarginRestsBaseResponseDto,
  type AccountsRestsBaseResponseDto,
} from '@/entities/account';
import {
  ACCOUNT_TYPE_ICONS,
  TRADING_ACCOUNT_TYPES,
  type TradingAccountType,
} from '@/widgets/trading-assets';
import { useTranslation } from 'react-i18next';
import { applyAmountPrecision, sum } from '@/shared/lib';

const TITLE_BY_SECTION: Record<TradingAccountType, string> = {
  invest: 'personalAccount.accountsSection.investTitle',
  spot: 'personalAccount.accountsSection.spotTitle',
  leverage: 'personalAccount.accountsSection.leverageTitle',
  earn: 'personalAccount.accountsSection.earnTitle',
};

type BlockType =
  | AccountsRestsBaseResponseDto
  | AccountsMarginRestsBaseResponseDto;

export const useApiTokensModalOptions = () => {
  const { t } = useTranslation();

  const { appSettingsData, isAppSettingsLoading, appSettingsError } =
    useAppSettingsDictionary();

  const {
    data: accountsRests,
    isLoading: isAccountsRestsLoading,
    error: accountsRestsError,
  } = useAccountsRests();

  const accessTypesMap = useMemo<Record<ApiTokenAccessKey, string>>(() => {
    const list = appSettingsData?.value.tradingWebapiTokenAccessList;
    return list ? Object.assign({}, ...list) : {};
  }, [appSettingsData]);

  const accounts = useMemo<AccountOption[]>(() => {
    if (!accountsRests) return [];

    const result: AccountOption[] = [];

    TRADING_ACCOUNT_TYPES.forEach(section => {
      const value = accountsRests[section];

      if (!value) return;

      const icon = ACCOUNT_TYPE_ICONS[section];
      const title = TITLE_BY_SECTION[section];

      const blocks: BlockType[] = Array.isArray(value) ? value : [value];

      blocks.forEach(block => {
        block.accounts?.forEach(acc => {
          if (acc.accTypeName !== ACCOUNT_TYPES.Trading) return;

          const amount =
            'assets' in acc && acc.assets?.length
              ? sum(
                  acc.assets.map(
                    asset =>
                      (asset.baseAvailable ?? 0) + (asset.baseReserved ?? 0)
                  )
                )
              : 'equity' in acc
                ? (acc.equity ?? 0)
                : 0;

          const precision =
            'balanceAsset' in acc && acc.balanceAsset?.precision
              ? acc.balanceAsset.precision
              : block.basePrecission;

          const assetCode =
            'balanceAsset' in acc && acc.balanceAsset?.code
              ? acc.balanceAsset.code
              : block.baseCode;

          result.push({
            icon,
            title: t(title),
            name: acc.accName || acc.accNum,
            balance: applyAmountPrecision(amount, precision, 'credit'),
            accountNumber: acc.accNum,
            accountType: section,
            baseCode: assetCode,
            basePrecision: precision,
            tradingAccKey: acc.tradingAccKey,
          });
        });
      });
    });

    return result;
  }, [accountsRests]);

  return {
    accessTypesMap,
    accounts,
    isLoading: isAppSettingsLoading || isAccountsRestsLoading,
    error: appSettingsError || accountsRestsError,
  };
};
